import React, { useState } from 'react';
import { Target, Check, Flower2, Users, MapPin, Banknote, Flame, Compass } from 'lucide-react';
import { SeasonalChallenge } from '../../data/go2LegendeMock';

interface SeasonalChallengesProps {
  challenges: SeasonalChallenge[];
}

const iconMap = {
  Flower2,
  Users,
  MapPin,
  Banknote,
  Flame,
  Compass,
};

const typeMap = {
  seasonal: 'Sezonski',
  social: 'Društveni',
  explorer: 'Istraživački',
  spender: 'Potrošački',
};

export const SeasonalChallenges: React.FC<SeasonalChallengesProps> = ({ challenges }) => {
  const [selectedType, setSelectedType] = useState<string>('all');

  const completedCount = challenges.filter((c) => c.completed).length;
  const totalCount = challenges.length;

  const filteredChallenges = selectedType === 'all'
    ? challenges
    : challenges.filter((c) => c.type === selectedType);

  const activeChallenges = filteredChallenges.filter((c) => !c.completed);
  const completedChallenges = filteredChallenges.filter((c) => c.completed);

  const tabs = [
    { id: 'all', label: 'Svi' },
    { id: 'seasonal', label: 'Sezonski' },
    { id: 'social', label: 'Društveni' },
    { id: 'explorer', label: 'Istraživački' },
    { id: 'spender', label: 'Potrošački' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-6 h-6 text-brand-500" />
          <h2 className="text-2xl font-bold text-gray-900">Aktivni Izazovi</h2>
        </div>
        <p className="text-gray-600">
          Završeno: {completedCount} / {totalCount}
        </p>
      </div>

      {/* Tab Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedType(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              selectedType === tab.id
                ? 'bg-brand-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active Challenges */}
      {activeChallenges.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {activeChallenges.map((challenge) => {
            const IconComponent = iconMap[challenge.icon as keyof typeof iconMap];
            const progressPercentage = (challenge.progress / challenge.target) * 100;

            return (
              <div
                key={challenge.id}
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center">
                    {IconComponent && <IconComponent className="w-6 h-6 text-brand-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-bold text-gray-900">{challenge.title}</h3>
                      <span className="px-2 py-1 bg-go2-blue/10 text-go2-blue text-xs font-medium rounded">
                        Aktivan
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{challenge.description}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-brand-500 h-full rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-gray-600">
                      {challenge.progress} / {challenge.target}
                    </span>
                    <span className="text-sm font-medium text-brand-500">
                      {progressPercentage.toFixed(0)}%
                    </span>
                  </div>
                </div>

                {/* Reward and Deadline */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-go2-pink font-semibold">
                    Nagrada: {challenge.reward} bodova
                  </span>
                  <span className="text-gray-500">
                    Rok: {new Date(challenge.deadline).toLocaleDateString('sr-RS')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Completed Challenges */}
      {completedChallenges.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Završeni Izazovi</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {completedChallenges.map((challenge) => {
              const IconComponent = iconMap[challenge.icon as keyof typeof iconMap];

              return (
                <div
                  key={challenge.id}
                  className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 opacity-75 relative overflow-hidden"
                >
                  {/* Completed Badge */}
                  <div className="absolute top-3 right-3">
                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded">
                      <Check className="w-3 h-3" />
                      Završeno!
                    </div>
                  </div>

                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                      {IconComponent && <IconComponent className="w-6 h-6 text-emerald-600" />}
                    </div>
                    <div className="flex-1 pr-20">
                      <h3 className="font-bold text-gray-900 mb-1">{challenge.title}</h3>
                      <p className="text-sm text-gray-600">{challenge.description}</p>
                    </div>
                  </div>

                  {/* Progress Bar (Full) */}
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div className="bg-emerald-500 h-full rounded-full" style={{ width: '100%' }} />
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-gray-600">
                        {challenge.target} / {challenge.target}
                      </span>
                      <span className="text-sm font-medium text-emerald-600">100%</span>
                    </div>
                  </div>

                  {/* Reward */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-emerald-600 font-semibold">
                      Osvojeno: {challenge.reward} bodova
                    </span>
                    <span className="text-gray-500 text-xs">
                      {typeMap[challenge.type]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredChallenges.length === 0 && (
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nema izazova u ovoj kategoriji</p>
        </div>
      )}
    </div>
  );
};
